import React from 'react';
import LegalLayout from '../components/LegalLayout';

export default function TermsPage() {
  return (
    <LegalLayout title="Terms and Conditions" lastUpdated="April 1, 2026">
      <p>
        Welcome to Longfloat (“Platform”, “Application”, “Service”, “we”, “our”, or “us”), owned and operated by Longfloat Information Technology Pvt Ltd.
      </p>
      <p>
        These Terms and Conditions (“Terms”) govern your access to and use of the Longfloat website, applications, APIs, and related services. By accessing or using Longfloat, you agree to comply with and be legally bound by these Terms.
      </p>
      <p>If you do not agree with these Terms, please discontinue use of the Service immediately.</p>

      <h2 id="eligibility">1. Eligibility</h2>
      <p>To use Longfloat, you must:</p>
      <ul>
        <li>Be at least 16 years old;</li>
        <li>Have the legal capacity to enter into binding agreements;</li>
        <li>Use the Service in compliance with applicable laws and regulations.</li>
      </ul>
      <p>By using the Service, you confirm that you meet these requirements.</p>

      <h2 id="account">2. Account Responsibilities</h2>
      <p>
        You are responsible for maintaining the confidentiality of your account credentials and for all activities conducted under your account.
      </p>
      <p>You agree to:</p>
      <ul>
        <li>Provide accurate and complete registration information;</li>
        <li>Keep your login credentials secure;</li>
        <li>
          Notify us immediately at{' '}
          <a href="mailto:info@longfloat.com">info@longfloat.com</a> if you
          suspect unauthorized access or security breaches.
        </li>
      </ul>
      <p>
        Longfloat Information Technology Pvt Ltd will not be responsible for losses caused by unauthorized use of your account.
      </p>

      <h2 id="user-content">3. User Content</h2>
      <p>
        You retain ownership of all content, files, prompts, workflows, messages, data, or materials uploaded, created, or generated through Longfloat (“User Content”).
      </p>
      <p>
        By using the Service, you grant Longfloat a limited, worldwide, non-exclusive license to host, process, store, transmit, and display User Content solely for:
      </p>
      <ul>
        <li>Operating the Service;</li>
        <li>Maintaining functionality;</li>
        <li>Improving platform performance;</li>
        <li>Providing requested features and integrations.</li>
      </ul>
      <p>We do not claim ownership of your User Content.</p>

      <h2 id="acceptable-use">4. Acceptable Use</h2>
      <p>You agree not to use Longfloat to:</p>
      <ul>
        <li>Violate any applicable law or regulation;</li>
        <li>Upload unlawful, harmful, defamatory, infringing, or abusive content;</li>
        <li>Attempt unauthorized access to systems, infrastructure, or accounts;</li>
        <li>Reverse engineer, decompile, or exploit the Service;</li>
        <li>Interfere with platform stability or security;</li>
        <li>Develop competing products using the Service;</li>
        <li>Distribute spam, malware, harmful code, or automated abuse.</li>
      </ul>
      <p>We reserve the right to suspend or terminate access for violations of these Terms.</p>

      <h2 id="ai-output">5. AI-Generated Output</h2>
      <p>
        Longfloat includes artificial intelligence and automation features that generate responses and content based on user inputs (“AI Output”).
      </p>
      <p>AI Output is provided on an “as is” basis and may contain inaccuracies or unintended results.</p>
      <p>You are solely responsible for:</p>
      <ul>
        <li>Reviewing AI Output before use;</li>
        <li>Verifying accuracy and compliance;</li>
        <li>Determining suitability for your intended purpose.</li>
      </ul>
      <p>
        Longfloat Information Technology Pvt Ltd does not guarantee that AI Output will be accurate, reliable, original, or non-infringing.
      </p>

      <h2 id="integrations">6. Third-Party Integrations</h2>
      <p>Longfloat may integrate with third-party services including, but not limited to:</p>
      <ul>
        <li>Slack</li>
        <li>Telegram</li>
        <li>WhatsApp</li>
        <li>Signal</li>
        <li>Google Drive</li>
        <li>Email providers</li>
        <li>External APIs and automation platforms</li>
      </ul>
      <p>Your use of third-party services is governed by their own terms and privacy policies.</p>
      <p>We are not responsible for the availability, content, security, or practices of third-party platforms.</p>

      <h2 id="billing">7. Subscriptions, Billing, and Refunds</h2>
      <p>Certain features of Longfloat may require a paid subscription.</p>
      <p>
        By subscribing, you authorize us to charge your selected payment method according to your chosen billing cycle.
      </p>

      <h3>Subscription Terms</h3>
      <ul>
        <li>Billing occurs in advance on a recurring basis;</li>
        <li>Plans renew automatically unless canceled;</li>
        <li>Pricing may change with prior notice.</li>
      </ul>

      <h3>Cancellation</h3>
      <p>
        You may cancel your subscription at any time through your account settings. Cancellation will take effect at the end of the active billing period.
      </p>

      <h3>Refunds</h3>
      <p>Except where required by applicable law, all payments are non-refundable.</p>

      <h2 id="free-plan">8. Free Plan</h2>
      <p>Longfloat may provide a free usage tier with limited functionality.</p>
      <p>
        We reserve the right to modify, restrict, or discontinue free plan features at any time without prior notice.
      </p>

      <h2 id="intellectual-property">9. Intellectual Property</h2>
      <p>All rights, title, and interest in Longfloat, including:</p>
      <ul>
        <li>Software,</li>
        <li>Design,</li>
        <li>Branding,</li>
        <li>Logos,</li>
        <li>APIs,</li>
        <li>Documentation,</li>
        <li>Proprietary technology,</li>
      </ul>
      <p>are owned by Longfloat Information Technology Pvt Ltd and protected by intellectual property laws.</p>
      <p>These Terms do not grant you ownership rights to any part of the Service.</p>

      <h2 id="termination">10. Termination</h2>
      <p>You may stop using Longfloat at any time.</p>
      <p>We may suspend or terminate your access immediately if:</p>
      <ul>
        <li>You violate these Terms;</li>
        <li>Required by law;</li>
        <li>Necessary to protect the Service or users;</li>
        <li>The Service is discontinued.</li>
      </ul>
      <p>Upon termination, your right to use the Service ends immediately.</p>
      <p>Where reasonably possible, we may allow export of User Content for up to 30 days after termination.</p>

      <h2 id="disclaimers">11. Disclaimer of Warranties</h2>
      <p>Longfloat is provided on an “as is” and “as available” basis without warranties of any kind.</p>
      <p>
        To the maximum extent permitted by law, Longfloat Information Technology Pvt Ltd disclaims all warranties, including:
      </p>
      <ul>
        <li>Merchantability,</li>
        <li>Fitness for a particular purpose,</li>
        <li>Non-infringement,</li>
        <li>Availability,</li>
        <li>Security,</li>
        <li>Accuracy,</li>
        <li>Reliability.</li>
      </ul>
      <p>We do not guarantee uninterrupted or error-free operation of the Service.</p>

      <h2 id="liability">12. Limitation of Liability</h2>
      <p>
        To the maximum extent permitted by applicable law, Longfloat Information Technology Pvt Ltd and its directors, employees, affiliates, contractors, and agents shall not be liable for:
      </p>
      <ul>
        <li>Indirect damages,</li>
        <li>Incidental damages,</li>
        <li>Consequential damages,</li>
        <li>Special damages,</li>
        <li>Loss of profits,</li>
        <li>Loss of data,</li>
        <li>Loss of goodwill.</li>
      </ul>
      <p>Our total liability arising from the Service shall not exceed:</p>
      <ul>
        <li>The amount paid by you to us during the previous 12 months; or</li>
        <li>USD $100,</li>
      </ul>
      <p>whichever is greater.</p>

      <h2 id="indemnification">13. Indemnification</h2>
      <p>
        You agree to defend, indemnify, and hold harmless Longfloat Information Technology Pvt Ltd from claims, damages, liabilities, losses, and expenses arising from:
      </p>
      <ul>
        <li>Your use of the Service;</li>
        <li>Your User Content;</li>
        <li>Your violation of these Terms;</li>
        <li>Your violation of any applicable law or third-party rights.</li>
      </ul>

      <h2 id="changes">14. Changes to These Terms</h2>
      <p>We may update these Terms from time to time.</p>
      <p>
        Updated versions will be posted with a revised “Last Updated” date. For material changes, we may notify users through email or in-app notifications.
      </p>
      <p>
        Continued use of the Service after changes become effective constitutes acceptance of the updated Terms.
      </p>

      <h2 id="governing-law">15. Governing Law and Jurisdiction</h2>
      <p>These Terms shall be governed by and interpreted in accordance with the laws of India.</p>
      <p>
        Any disputes arising from these Terms or the use of Longfloat shall be subject to the exclusive jurisdiction of the courts located in India.
      </p>

      <h2 id="contact">16. Contact Information</h2>
      <p>For questions regarding these Terms and Conditions, contact:</p>
      <p className="legal-address">
        <span>Longfloat Information Technology Pvt Ltd</span>
        <span>
          Email:{' '}
          <a href="mailto:info@longfloat.com">info@longfloat.com</a>
        </span>
        <span>Application: Longfloat</span>
      </p>
    </LegalLayout>
  );
}
